import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCampaigns } from '../../../../reducers/hubspotReducer';
import { HubspotCampaignCtx } from './campaign-dialog/HubspotCampaignCtx';
import { HubspotFilterTypes } from './campaign-dialog/new/HubspotCanvasPanel';
import { FilterComponentType, FilterItem } from '../../../common/filters/filters-renderer/types';
import { FiltersRenderer } from '../../../common/filters/filters-renderer/FiltersRenderer';
import { useHubspotFiltersContext } from './HubspotFiltersContext';
import { Campaign } from './campaign-dialog/campaignTypes';

const enrollmentRuleFilterItems = [
  { label: 'With Enrollment Rules', value: 'with_enrollment_rule' },
  { label: 'Without Enrollment Rules', value: 'without_enrollment_rule' },
];

export const HubspotFilters = ({ withPanelWrapper }: { withPanelWrapper?: boolean }) => {
  const campaigns: Campaign[] | undefined = useSelector(selectCampaigns);

  const { campaignStatusOptions, campaignTypeOptions } = useContext(HubspotCampaignCtx);
  const { filterValues, setFilterValue, clearAllFilterValues } = useHubspotFiltersContext();

  const filters: { [key: string]: FilterItem } = useMemo(() => {
    const allStatuses: string[] = (campaigns || [])
      .map((campaign) => campaign.Status)
      .filter((status) => status !== undefined)
      .sort();

    const usedStatuses = Array.from(new Set(allStatuses));
    const statusItems = usedStatuses.map((status) => ({
      label:
        campaignStatusOptions.find((option) => option.name === status)?.label ||
        status ||
        '(no status)',
      value: status || '',
    }));

    const usedTypes = Array.from(
      new Set((campaigns || []).map((campaign) => campaign.Type)),
    ).sort();
    const typeItems = usedTypes.map((type) => ({
      label:
        campaignTypeOptions.find((option) => option.name === type)?.label || type || '(no type)',
      value: type || '',
    }));

    return {
      [HubspotFilterTypes.ACTIVITY]: {
        type: FilterComponentType.MULTI_SELECT,
        items: [
          { label: 'Active', value: 'active' },
          { label: 'Inactive', value: 'inactive' },
        ],
        label: 'Campaign Activity',
      },
      [HubspotFilterTypes.CAMPAIGN_STATUS]: {
        type: FilterComponentType.MULTI_SELECT,
        items: statusItems,
        label: 'Campaign Status',
      },
      [HubspotFilterTypes.CAMPAIGN_TYPE]: {
        type: FilterComponentType.MULTI_SELECT,
        items: typeItems,
        label: 'Campaign Type',
      },
      [HubspotFilterTypes.ENROLLMENT_RULE]: {
        type: FilterComponentType.MULTI_SELECT,
        items: enrollmentRuleFilterItems,
        label: 'Enrollment Rule',
      },
    };
  }, [campaignStatusOptions, campaignTypeOptions, campaigns]);

  return (
    <FiltersRenderer
      filters={filters}
      filterValues={filterValues}
      onFilterChange={setFilterValue}
      onClearAll={clearAllFilterValues}
      withPanelWrapper={withPanelWrapper}
    />
  );
};
